import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useRefetchOnFocus } from "@/hooks/useRefetchOnFocus";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Eye, FilePdf, DownloadSimple, ShareNetwork, PencilSimple, FloppyDisk, X as XIcon, Trash, ArrowCounterClockwise, CircleNotch, ArrowsClockwise, LinkSimple, Warning } from "@phosphor-icons/react";
import UnifiedReportView from "@/components/UnifiedReportView";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import ResizableDialogContent from "@/components/ResizableDialog";
import { useAuth } from "@/contexts/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import ShareLinksDialog from "@/components/ShareLinksDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = { candidate: "Candidato", employee: "Dipendente", management: "Responsabile" };

function daysRemainingInTrash(deletedAtIso) {
  if (!deletedAtIso) return 30;
  try {
    const elapsedMs = Date.now() - new Date(deletedAtIso).getTime();
    return Math.max(0, 30 - Math.floor(elapsedMs / 86400000));
  } catch { return 30; }
}

export default function ReportsList({ title, filterTypes, emptyText, testIdPrefix = "reports" }) {
  const { activeWorkspaceId, user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [activeReports, setActiveReports] = useState([]);
  const [trashedReports, setTrashedReports] = useState([]);
  const [activeTab, setActiveTab] = useState("attivi");
  const [viewReport, setViewReport] = useState(null);
  const [subject, setSubject] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareHtml, setShareHtml] = useState("");
  const [shareId, setShareId] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editedHtml, setEditedHtml] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [regenerating, setRegenerating] = useState(false);
  const [shareLinksResource, setShareLinksResource] = useState(null);
  const htmlRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const [act, trash] = await Promise.all([
        axios.get(`${API}/reports?status=attivi`, { withCredentials: true }),
        axios.get(`${API}/reports?status=cestino`, { withCredentials: true }),
      ]);
      setActiveReports((act.data || []).filter(x => filterTypes.includes(x.type)));
      setTrashedReports((trash.data || []).filter(x => filterTypes.includes(x.type)));
    } catch { /* noop */ }
  }, [filterTypes]);

  // Batch 62.A: re-load on workspace switch for agency accounts
  useEffect(() => { reload(); }, [reload, activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps
  // B71 FIX-3: refetch on focus
  useRefetchOnFocus(reload);

  // BUG-60: polling per card in 'generating' → riabilita bottoni quando ready
  useEffect(() => {
    const anyGenerating = activeReports.some(r => r.generation_status === "generating");
    if (!anyGenerating) return undefined;
    const t = setInterval(() => reload(), 5000);
    return () => clearInterval(t);
  }, [activeReports, reload]);

  // BUG-60: counter elapsed time per card generating
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const elapsedSec = (startedAt) => {
    if (!startedAt) return 0;
    try {
      // tick referenced to force re-render every second
      void tick;
      return Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
    } catch { return 0; }
  };

  const downloadPdf = async (rid, name) => {
    try {
      const response = await axios.get(`${API}/reports/${rid}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${name.replace(/\s/g,'_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF scaricato");
    } catch { toast.error("Errore download PDF"); }
  };

  const moveToTrash = async (rid) => {
    try {
      await axios.delete(`${API}/reports/${rid}`, { withCredentials: true });
      toast.success("Report spostato nel cestino");
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const handleTrashClick = (r) => {
    if (window.confirm(`Spostare il report di "${r.name}" nel cestino?\nPotrai ripristinarlo entro 30 giorni.`)) {
      moveToTrash(r.id);
    }
  };

  const restoreFromTrash = async (rid) => {
    try {
      await axios.post(`${API}/reports/${rid}/restore`, {}, { withCredentials: true });
      toast.success("Report ripristinato");
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  // B79 P1.3 — Selective regeneration (single + batch multi-select).
  const regenerateOne = async (r) => {
    if (!r) return { ok: false };
    const isEmp = !!r.employee_id;
    const entityId = r.employee_id || r.candidate_id;
    if (!entityId) return { ok: false, name: r.name };
    const path = isEmp ? "employees" : "candidates";
    try {
      await axios.post(`${API}/${path}/${entityId}/regenerate-report`, {}, { withCredentials: true });
      return { ok: true, name: r.name };
    } catch (e) {
      return { ok: false, name: r.name, error: e.response?.data?.detail || "Errore" };
    }
  };

  const handleSingleRegenerate = async (r) => {
    if (r.generation_status === "generating") return;
    if (!window.confirm(`Rigenerare il report di "${r.name}"?\nVerra creata una nuova versione con la logica corrente del sistema.`)) return;
    setRegenerating(true);
    const res = await regenerateOne(r);
    setRegenerating(false);
    if (res.ok) toast.success(`Rigenerazione avviata: ${res.name}`);
    else toast.error(`Errore su "${res.name}": ${res.error || ""}`);
    await reload();
  };

  const toggleSelected = (rid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid); else next.add(rid);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectAllVisible = () => {
    const eligible = activeReports.filter(r => r.generation_status !== "generating").map(r => r.id);
    setSelectedIds(new Set(eligible));
  };

  const handleBatchRegenerate = async () => {
    const selectedReports = activeReports.filter(r => selectedIds.has(r.id) && r.generation_status !== "generating");
    if (selectedReports.length === 0) { toast.info("Nessun report selezionato."); return; }
    if (!window.confirm(`Rigenerare ${selectedReports.length} report selezionati?\nOgni rigenerazione consuma una quota (per utenti non admin).`)) return;
    setRegenerating(true);
    let ok = 0, fail = 0;
    for (const r of selectedReports) {
      const res = await regenerateOne(r);
      if (res.ok) ok += 1; else fail += 1;
    }
    setRegenerating(false);
    clearSelection();
    if (fail === 0) toast.success(`Rigenerazione avviata su ${ok} report`);
    else toast.warning(`Rigenerazione: ${ok} ok, ${fail} errore`);
    await reload();
  };

  const createShareable = async (rid) => {
    try {
      const { data } = await axios.post(`${API}/reports/${rid}/share`, {}, { withCredentials: true });
      setShareId(data.share_id);
      const report = await axios.get(`${API}/shared-report/${data.share_id}`);
      setShareHtml(report.data.html || "");
      setShareOpen(true);
      toast.success("Versione condivisibile creata");
    } catch { toast.error("Errore creazione versione condivisibile"); }
  };

  const saveShareable = async () => {
    if (!viewReport || !shareId) return;
    try {
      await axios.put(`${API}/reports/${viewReport.id}/share/${shareId}`, { html: shareHtml }, { withCredentials: true });
      toast.success("Versione condivisibile salvata");
    } catch { toast.error("Errore salvataggio"); }
  };

  const entityTypeOf = (rep) => {
    if (!rep) return "candidate";
    if (rep.type === "management") return "manager";
    if (rep.type === "employee") return "employee";
    return "candidate";
  };
  const entityIdOf = (rep) => rep?.employee_id || rep?.candidate_id || null;

  const openReport = async (rid) => {
    try {
      const { data } = await axios.get(`${API}/reports/${rid}`, { withCredentials: true });
      setViewReport(data);
      setEditMode(false);
      setEditedHtml(data.html || "");
      const eid = data.employee_id || data.candidate_id;
      if (eid) {
        const path = data.type === "candidate" ? "candidates" : "employees";
        try {
          const sub = await axios.get(`${API}/${path}/${eid}`, { withCredentials: true });
          setSubject(sub.data || null);
        } catch { setSubject(null); }
      } else {
        setSubject(null);
      }
    } catch { /* noop */ }
  };

  const saveInlineEdit = async () => {
    if (!viewReport || !htmlRef.current) return;
    const newHtml = htmlRef.current.innerHTML;
    try {
      const resp = await axios.post(`${API}/reports/${viewReport.id}/share`, {}, { withCredentials: true });
      const sid = resp.data.share_id;
      await axios.put(`${API}/reports/${viewReport.id}/share/${sid}`, { html: newHtml }, { withCredentials: true });
      setShareId(sid);
      setEditMode(false);
      toast.success("Modifiche salvate come versione condivisibile");
      const url = `${window.location.origin}/shared-report/${sid}`;
      navigator.clipboard.writeText(url).catch(() => {});
      toast.info(`Link copiato: ${url}`);
    } catch { toast.error("Errore salvataggio"); }
  };

  const list = activeTab === "cestino" ? trashedReports : activeReports;

  return (
    <div className="animate-fade-in" data-testid={`${testIdPrefix}-page`}>
      <PageHeader title={title} />
      <ShareLinksDialog
        open={!!shareLinksResource}
        onOpenChange={(o) => { if (!o) setShareLinksResource(null); }}
        resource={shareLinksResource}
      />

      {/* Tabs */}
      <div className="flex border-b border-[#0D1F3C]/10 mb-4" data-testid={`${testIdPrefix}-tabs`}>
        <button
          onClick={() => setActiveTab("attivi")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "attivi" ? "border-[#1B4FD8] text-[#1B4FD8]" : "border-transparent text-[#0D1F3C]/60 hover:text-[#0D1F3C]"
          }`}
          data-testid={`${testIdPrefix}-tab-attivi`}
        >
          Attivi <span className="ml-1 text-xs text-[#0D1F3C]/40">{activeReports.length}</span>
        </button>
        <button
          onClick={() => setActiveTab("cestino")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "cestino" ? "border-[#1B4FD8] text-[#1B4FD8]" : "border-transparent text-[#0D1F3C]/60 hover:text-[#0D1F3C]"
          }`}
          data-testid={`${testIdPrefix}-tab-cestino`}
        >
          Cestino <span className="ml-1 text-xs text-[#0D1F3C]/40">{trashedReports.length}</span>
        </button>
      </div>

      <Dialog open={!!viewReport} onOpenChange={() => { setViewReport(null); setSubject(null); }}>
        <ResizableDialogContent defaultW={950} defaultH={700}>
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Report - {viewReport?.name}</DialogTitle>
              <div className="flex items-center gap-2">
                {viewReport && !editMode && (
                  <Button variant="outline" size="sm" onClick={() => setEditMode(true)} data-testid="edit-report-btn" className="border-[#F59E0B]/40 text-[#F59E0B]">
                    <PencilSimple size={16} className="mr-1" /> Modifica
                  </Button>
                )}
                {viewReport && editMode && (
                  <>
                    <Button variant="outline" size="sm" onClick={saveInlineEdit} data-testid="save-inline-btn" className="border-[#10B981]/40 text-[#10B981]">
                      <FloppyDisk size={16} className="mr-1" /> Salva
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setEditMode(false); setEditedHtml(viewReport.html || ""); }} data-testid="cancel-inline-btn">
                      <XIcon size={16} className="mr-1" /> Annulla
                    </Button>
                  </>
                )}
                {viewReport && !editMode && <Button variant="outline" size="sm" onClick={() => createShareable(viewReport.id)} data-testid="share-report-btn" className="border-[#10B981]/30 text-[#10B981]"><ShareNetwork size={16} className="mr-1" />Condividi</Button>}
                {viewReport && !editMode && <Button variant="outline" size="sm" onClick={() => downloadPdf(viewReport.id, viewReport.name)} data-testid="download-pdf-btn" className="border-[#1B4FD8]/30 text-[#1B4FD8]"><DownloadSimple size={16} className="mr-1" />PDF</Button>}
              </div>
            </div>
          </DialogHeader>

          {viewReport && editMode && (
            <div
              ref={htmlRef}
              className="mt-4 prose prose-sm max-w-none ring-2 ring-[#F59E0B]/40 p-3 rounded-md bg-amber-50/30"
              contentEditable
              suppressContentEditableWarning
              dangerouslySetInnerHTML={{ __html: editedHtml }}
            />
          )}

          {viewReport && !editMode && (
            <div className="mt-4">
              <UnifiedReportView
                report={viewReport}
                subject={subject || { first_name: viewReport.name, last_name: "", hr_notes: "" }}
                entityType={entityTypeOf(viewReport)}
                entityId={entityIdOf(viewReport)}
                onNotesUpdated={(newNotes) => setSubject(s => s ? { ...s, hr_notes: newNotes } : s)}
              />
            </div>
          )}
        </ResizableDialogContent>
      </Dialog>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <ResizableDialogContent defaultW={950} defaultH={700}>
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Versione Condivisibile</DialogTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={saveShareable} className="border-[#10B981]/30 text-[#10B981]">Salva Modifiche</Button>
                <Button variant="outline" size="sm" onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/shared-report/${shareId}`);
                  toast.success("Link copiato");
                }} className="border-[#1B4FD8]/30 text-[#1B4FD8]">Copia Link</Button>
              </div>
            </div>
          </DialogHeader>
          <p className="text-xs text-[#0D1F3C]/50 mb-3">Modifica il testo del report prima di condividerlo. Le modifiche non influenzano il report originale.</p>
          <div className="border border-[#0D1F3C]/10 rounded-lg p-4 min-h-[300px] prose prose-sm max-w-none"
            contentEditable suppressContentEditableWarning
            onBlur={(e) => setShareHtml(e.currentTarget.innerHTML)}
            dangerouslySetInnerHTML={{ __html: shareHtml }}
            data-testid="share-editor" />
        </ResizableDialogContent>
      </Dialog>

      <div className="space-y-3">
        {activeTab === "attivi" && activeReports.length > 0 && (
          <div className="flex items-center justify-between p-3 rounded-md bg-[#F8FAFC] border border-[#0D1F3C]/8" data-testid="reports-batch-toolbar">
            <div className="flex items-center gap-3 text-sm text-[#0D1F3C]/70">
              {selectedIds.size > 0 ? (
                <>
                  <span className="font-medium text-[#0D1F3C]" data-testid="reports-selected-count">
                    {selectedIds.size} {selectedIds.size === 1 ? "selezionato" : "selezionati"}
                  </span>
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="text-xs underline text-[#0D1F3C]/50 hover:text-[#0D1F3C]"
                    data-testid="reports-clear-selection"
                  >Annulla selezione</button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={selectAllVisible}
                  className="text-xs underline text-[#0D1F3C]/50 hover:text-[#0D1F3C]"
                  data-testid="reports-select-all"
                >Seleziona tutti</button>
              )}
            </div>
            <Button
              size="sm"
              onClick={handleBatchRegenerate}
              disabled={selectedIds.size === 0 || regenerating}
              className="bg-[#1B4FD8] hover:bg-[#1B4FD8]/90 text-white gap-1 disabled:opacity-40"
              data-testid="reports-batch-regenerate"
            >
              <ArrowsClockwise size={14} className={regenerating ? "animate-spin" : ""} />
              Rigenera selezionati
            </Button>
          </div>
        )}
        {list.length === 0 ? (
          <Card className="border-[#0D1F3C]/8" data-testid={`${testIdPrefix}-empty`}>
            <CardContent className="py-12 text-center text-[#0D1F3C]/40 text-sm">
              {activeTab === "cestino" ? "Cestino vuoto" : emptyText}
            </CardContent>
          </Card>
        ) : list.map(r => {
          const isGenerating = r.generation_status === "generating";
          const isSelected = selectedIds.has(r.id);
          return (
          <Card key={r.id} className={`border-[#0D1F3C]/8 hover:border-[#1B4FD8]/30 hover:shadow-md transition-all ${isSelected ? "ring-2 ring-[#1B4FD8]/40 border-[#1B4FD8]/40" : ""}`} data-testid={`report-${r.id}`}>
            <CardContent className="p-5 flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                {activeTab === "attivi" && (
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => !isGenerating && toggleSelected(r.id)}
                    disabled={isGenerating}
                    aria-label={`Seleziona report di ${r.name}`}
                    data-testid={`report-checkbox-${r.id}`}
                  />
                )}
                <div className="flex-1">
                  <h3 className="font-medium text-[#0D1F3C] flex items-center gap-2 flex-wrap">
                    {r.name}
                    {/* B82 P0.1 follow-up: badge SOLO admin, per non allarmare
                        gli HR con report che potrebbero essere falsi positivi. */}
                    {isAdmin && r.scoring_version !== "b72.3" && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 uppercase tracking-wide"
                        title="Report generato con motore pre-HMAC. I punteggi possono non corrispondere alle risposte reali. Da rigenerare."
                        data-testid={`pre-hmac-badge-${r.id}`}
                      >
                        <Warning size={10} weight="fill" />
                        Calcolo non affidabile
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-[#0D1F3C]/50">
                    {TYPE_LABELS[r.type] || r.type} · {new Date(r.created_at).toLocaleDateString('it-IT')}
                  </p>
                  {activeTab === "cestino" && r.deleted_at && (
                    <p className="text-xs text-amber-700 mt-1">
                      Eliminato {new Date(r.deleted_at).toLocaleDateString('it-IT')} · cancellazione definitiva tra {daysRemainingInTrash(r.deleted_at)} giorni
                    </p>
                  )}
                  {isGenerating && activeTab === "attivi" && (
                    <div className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#0D1F3C]/8 text-[#0D1F3C] text-xs font-medium" data-testid={`report-generating-${r.id}`}>
                      <CircleNotch size={12} className="animate-spin" />
                      In elaborazione · {elapsedSec(r.generation_started_at)}s
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {activeTab === "attivi" ? (
                  <>
                    <Button variant="outline" size="sm"
                      onClick={() => !isGenerating && openReport(r.id)}
                      disabled={isGenerating}
                      title={isGenerating ? "Report in elaborazione" : ""}
                      className={isGenerating ? "opacity-50 cursor-not-allowed" : ""}
                      data-testid={`view-report-${r.id}`}>
                      <Eye size={16} className="mr-1" /> Visualizza
                    </Button>
                    <Button variant="outline" size="sm"
                      onClick={() => !isGenerating && downloadPdf(r.id, r.name)}
                      disabled={isGenerating}
                      title={isGenerating ? "Report in elaborazione" : ""}
                      data-testid={`pdf-report-${r.id}`}
                      className={`border-[#1B4FD8]/20 text-[#1B4FD8] hover:bg-[#1B4FD8]/5 ${isGenerating ? "opacity-50 cursor-not-allowed" : ""}`}>
                      <FilePdf size={16} className="mr-1" /> PDF
                    </Button>
                    <Button variant="outline" size="sm"
                      onClick={() => handleSingleRegenerate(r)}
                      disabled={isGenerating || regenerating}
                      title={isGenerating ? "Report in elaborazione" : "Rigenera con la logica corrente"}
                      data-testid={`regenerate-report-${r.id}`}
                      className={`border-[#7C3AED]/30 text-[#7C3AED] hover:bg-[#7C3AED]/5 ${(isGenerating || regenerating) ? "opacity-50 cursor-not-allowed" : ""}`}>
                      <ArrowsClockwise size={16} className="mr-1" /> Rigenera
                    </Button>
                    <Button variant="outline" size="sm"
                      onClick={() => setShareLinksResource({ id: r.id, type: "report", name: r.name })}
                      disabled={isGenerating}
                      title="Gestisci link condivisi (crea, copia, revoca)"
                      data-testid={`share-links-btn-${r.id}`}
                      className={`border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/5 ${isGenerating ? "opacity-50 cursor-not-allowed" : ""}`}>
                      <LinkSimple size={16} className="mr-1" /> Link
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleTrashClick(r)} data-testid={`trash-report-${r.id}`}
                      className="text-red-600 hover:bg-red-50" title="Sposta nel cestino">
                      <Trash size={16} />
                    </Button>
                  </>
                ) : (
                  <Button size="sm" onClick={() => restoreFromTrash(r.id)} data-testid={`restore-report-${r.id}`}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1">
                    <ArrowCounterClockwise size={14} /> Ripristina
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>
    </div>
  );
}
